import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
  AreaChart,
  Area,
} from 'recharts';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockResetIcon from '@mui/icons-material/LockReset';
import {
  getAnalyticsOverview,
  getAnalyticsActiveUsers,
  getAnalyticsPopularEndpoints,
  getAnalyticsPopularSymbols,
  getAnalyticsDeviceSplit,
  getAnalyticsAuthMethods,
  getAnalyticsGeo,
  getAnalyticsPeakHours,
  getAnalyticsRetention,
  getAnalyticsUsers,
  adminSetPassword,
} from '../../api/admin';
import DWLFLoader from '../../components/DWLFLoader';
import useSchemeColors from '../../hooks/useSchemeColors';


const formatDuration = (milliseconds) => {
  if (!milliseconds) return '0s';
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
};

const formatDateTime = (timestamp) => {
  if (!timestamp) return '—';
  return new Date(timestamp).toLocaleString();
};

const MetricCard = ({ title, value, subtitle, loading, icon }) => (
  <Card>
    <CardContent>
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {icon}
          <Typography variant="h6" component="div">
            {title}
          </Typography>
        </Stack>
        {loading ? (
          <DWLFLoader size="small" showText={false} />
        ) : (
          <>
            <Typography variant="h4" component="div" color="primary">
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </>
        )}
      </Stack>
    </CardContent>
  </Card>
);

const PeakHoursHeatmap = ({ data, loading }) => {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <DWLFLoader size="medium" />
      </Box>
    );
  }

  if (!data?.length) {
    return (
      <Typography variant="body2" color="text.secondary" align="center" p={4}>
        No peak hours data available
      </Typography>
    );
  }

  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const hours = Array.from({ length: 24 }, (_, i) => i);
  
  // Create lookup map for quick access
  const heatmapData = {};
  data.forEach(item => {
    heatmapData[`${item.row}-${item.col}`] = item.value;
  });

  // Find max value for color scaling
  const maxValue = Math.max(...data.map(item => item.value), 1);

  return (
    <Box sx={{ overflowX: 'auto', p: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 60 }}>Day</TableCell>
            {hours.map(hour => (
              <TableCell key={hour} sx={{ minWidth: 30, textAlign: 'center', fontSize: '0.75rem' }}>
                {hour}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {days.map(day => (
            <TableRow key={day}>
              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>{day}</TableCell>
              {hours.map(hour => {
                const value = heatmapData[`${day}-${hour}`] || 0;
                const intensity = value / maxValue;
                const backgroundColor = value === 0 ? 'var(--dwlf-ink-04)' : `rgb(var(--dwlf-accent-rgb) / ${0.1 + intensity * 0.7})`;
                return (
                  <TableCell 
                    key={hour} 
                    sx={{ 
                      backgroundColor,
                      textAlign: 'center',
                      fontSize: '0.7rem',
                      color: intensity > 0.5 ? 'var(--dwlf-on-accent)' : 'inherit',
                      p: 0.5,
                    }}
                    title={`${day} ${hour}:00 - ${value} requests`}
                  >
                    {value > 0 ? value : ''}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

const Analytics = () => {
  // Recharts writes SVG attributes: resolved Signal colours (DWLF-298).
  const c = useSchemeColors();
  const COLORS = [c.accent, c.up, c.warn, c.down, c['accent-lo'], c.ink(0.6)];
  const axis = { stroke: c.ink(0.5), tick: { fill: c.ink(0.6), fontSize: 12 } };
  const [loading, setLoading] = useState({
    overview: false,
    activeUsers: false,
    endpoints: false,
    symbols: false,
    devices: false,
    authMethods: false,
    geo: false,
    peakHours: false,
    retention: false,
    users: false,
  });

  const [data, setData] = useState({
    overview: null,
    activeUsers: null,
    endpoints: null,
    symbols: null,
    devices: null,
    authMethods: null,
    geo: null,
    peakHours: null,
    retention: null,
    users: null,
  });

  const [error, setError] = useState('');
  const [passwordDialog, setPasswordDialog] = useState({ open: false, accountId: '', email: '' });
  const [newPassword, setNewPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });
  const [dateRange, setDateRange] = useState('30'); // days
  const [customFromDate, setCustomFromDate] = useState('');
  const [customToDate, setCustomToDate] = useState('');

  const calculateDateRange = useCallback(() => {
    if (dateRange === 'custom') {
      return { from: customFromDate, to: customToDate };
    }
    
    const now = new Date();
    const daysAgo = parseInt(dateRange);
    // Subtract an extra day to account for inclusive range (today counts as day 1)
    const fromDate = new Date(now.getTime() - ((daysAgo - 1) * 24 * 60 * 60 * 1000));
    
    return {
      from: fromDate.toISOString().split('T')[0],
      to: now.toISOString().split('T')[0],
    };
  }, [dateRange, customFromDate, customToDate]);

  const loadAnalytics = useCallback(async () => {
    setError('');
    const { from, to } = calculateDateRange();
    
    // Load all analytics data and collect any errors
    const promises = [
      loadOverview(from, to),
      loadActiveUsers(from, to),
      loadPopularEndpoints(from, to),
      loadPopularSymbols(from, to),
      loadDeviceSplit(from, to),
      loadAuthMethods(from, to),
      loadGeo(from, to),
      loadPeakHours(from, to),
      loadRetention(from, to),
      loadUsers(from, to),
    ];

    const results = await Promise.allSettled(promises);
    const errors = results
      .filter(result => result.status === 'rejected')
      .map(result => result.reason?.message || 'Unknown error');
    
    if (errors.length > 0) {
      setError(`Failed to load some analytics data: ${errors.join(', ')}`);
    }
  }, [calculateDateRange]);

  const loadOverview = async (from, to) => {
    setLoading(prev => ({ ...prev, overview: true }));
    try {
      const response = await getAnalyticsOverview({ from, to });
      setData(prev => ({ ...prev, overview: response }));
    } catch (err) {
      console.error('Error loading overview:', err);
      throw new Error(`Overview: ${err.message || 'Failed to load'}`);
    } finally {
      setLoading(prev => ({ ...prev, overview: false }));
    }
  };

  const loadActiveUsers = async (from, to) => {
    setLoading(prev => ({ ...prev, activeUsers: true }));
    try {
      const response = await getAnalyticsActiveUsers({ from, to });
      setData(prev => ({ ...prev, activeUsers: response?.data || [] }));
    } catch (err) {
      console.error('Error loading active users:', err);
      throw new Error(`Active Users: ${err.message || 'Failed to load'}`);
    } finally {
      setLoading(prev => ({ ...prev, activeUsers: false }));
    }
  };

  const loadPopularEndpoints = async (from, to) => {
    setLoading(prev => ({ ...prev, endpoints: true }));
    try {
      const response = await getAnalyticsPopularEndpoints({ from, to, limit: 10 });
      setData(prev => ({ ...prev, endpoints: response?.data || [] }));
    } catch (err) {
      console.error('Error loading endpoints:', err);
      throw new Error(`Endpoints: ${err.message || 'Failed to load'}`);
    } finally {
      setLoading(prev => ({ ...prev, endpoints: false }));
    }
  };

  const loadPopularSymbols = async (from, to) => {
    setLoading(prev => ({ ...prev, symbols: true }));
    try {
      const response = await getAnalyticsPopularSymbols({ from, to, limit: 10 });
      setData(prev => ({ ...prev, symbols: response?.data || [] }));
    } catch (err) {
      console.error('Error loading symbols:', err);
      throw new Error(`Symbols: ${err.message || 'Failed to load'}`);
    } finally {
      setLoading(prev => ({ ...prev, symbols: false }));
    }
  };

  const loadDeviceSplit = async (from, to) => {
    setLoading(prev => ({ ...prev, devices: true }));
    try {
      const response = await getAnalyticsDeviceSplit({ from, to });
      setData(prev => ({ ...prev, devices: response?.data || [] }));
    } catch (err) {
      console.error('Error loading device split:', err);
      throw new Error(`Device Split: ${err.message || 'Failed to load'}`);
    } finally {
      setLoading(prev => ({ ...prev, devices: false }));
    }
  };

  const loadAuthMethods = async (from, to) => {
    setLoading(prev => ({ ...prev, authMethods: true }));
    try {
      const response = await getAnalyticsAuthMethods({ from, to });
      setData(prev => ({ ...prev, authMethods: response?.data || [] }));
    } catch (err) {
      console.error('Error loading auth methods:', err);
      throw new Error(`Auth Methods: ${err.message || 'Failed to load'}`);
    } finally {
      setLoading(prev => ({ ...prev, authMethods: false }));
    }
  };

  const loadGeo = async (from, to) => {
    setLoading(prev => ({ ...prev, geo: true }));
    try {
      const response = await getAnalyticsGeo({ from, to });
      setData(prev => ({ ...prev, geo: response?.data || [] }));
    } catch (err) {
      console.error('Error loading geo data:', err);
      throw new Error(`Geo Data: ${err.message || 'Failed to load'}`);
    } finally {
      setLoading(prev => ({ ...prev, geo: false }));
    }
  };

  const loadPeakHours = async (from, to) => {
    setLoading(prev => ({ ...prev, peakHours: true }));
    try {
      const response = await getAnalyticsPeakHours({ from, to });
      setData(prev => ({ ...prev, peakHours: response?.data || [] }));
    } catch (err) {
      console.error('Error loading peak hours:', err);
      throw new Error(`Peak Hours: ${err.message || 'Failed to load'}`);
    } finally {
      setLoading(prev => ({ ...prev, peakHours: false }));
    }
  };

  const loadRetention = async (from, to) => {
    setLoading(prev => ({ ...prev, retention: true }));
    try {
      const response = await getAnalyticsRetention({ from, to });
      setData(prev => ({ ...prev, retention: response?.data || [] }));
    } catch (err) {
      console.error('Error loading retention:', err);
      throw new Error(`Retention: ${err.message || 'Failed to load'}`);
    } finally {
      setLoading(prev => ({ ...prev, retention: false }));
    }
  };

  const loadUsers = async (from, to) => {
    setLoading(prev => ({ ...prev, users: true }));
    try {
      const response = await getAnalyticsUsers({ from, to });
      setData(prev => ({ ...prev, users: response?.data || [] }));
    } catch (err) {
      console.error('Error loading users:', err);
      throw new Error(`Users: ${err.message || 'Failed to load'}`);
    } finally {
      setLoading(prev => ({ ...prev, users: false }));
    }
  };

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const handleSetPassword = async () => {
    if (newPassword.length < 8) {
      setSnackbar({ open: true, severity: 'error', message: 'Password must be at least 8 characters' });
      return;
    }
    setPasswordLoading(true);
    const targetAccountId = passwordDialog.accountId;
    const targetEmail = passwordDialog.email;
    try {
      await adminSetPassword(targetAccountId, newPassword);
      setSnackbar({ open: true, severity: 'success', message: `Password updated for ${targetEmail || targetAccountId}` });
      // Only close dialog if it's still showing the same user
      setPasswordDialog(prev => prev.accountId === targetAccountId ? { open: false, accountId: '', email: '' } : prev);
      setNewPassword('');
    } catch (err) {
      setSnackbar({ open: true, severity: 'error', message: err?.response?.data?.error || err.message || 'Failed to set password' });
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <Box>
      {/* Header Controls */}
      <Stack direction="row" spacing={2} alignItems="center" mb={3}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Date Range</InputLabel>
          <Select value={dateRange} label="Date Range" onChange={(e) => setDateRange(e.target.value)}>
            <MenuItem value="1">Last 24 hours</MenuItem>
            <MenuItem value="7">Last 7 days</MenuItem>
            <MenuItem value="30">Last 30 days</MenuItem>
            <MenuItem value="90">Last 90 days</MenuItem>
            <MenuItem value="custom">Custom</MenuItem>
          </Select>
        </FormControl>

        {dateRange === 'custom' && (
          <>
            <TextField
              size="small"
              type="date"
              label="From"
              value={customFromDate}
              onChange={(e) => setCustomFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              type="date"
              label="To"
              value={customToDate}
              onChange={(e) => setCustomToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </>
        )}

        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadAnalytics}
          disabled={Object.values(loading).some(Boolean)}
        >
          Refresh
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Overview Metrics */}
      <Grid container spacing={3} mb={4}>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <MetricCard
            title="DAU"
            value={data.overview?.dau || 0}
            subtitle="Daily Active Users"
            loading={loading.overview}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <MetricCard
            title="WAU"
            value={data.overview?.wau || 0}
            subtitle="Weekly Active Users"
            loading={loading.overview}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <MetricCard
            title="MAU"
            value={data.overview?.mau || 0}
            subtitle="Monthly Active Users"
            loading={loading.overview}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <MetricCard
            title="Total Requests"
            value={data.overview?.totalRequests?.toLocaleString() || 0}
            loading={loading.overview}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <MetricCard
            title="Avg Session"
            value={formatDuration(data.overview?.avgSessionDuration)}
            loading={loading.overview}
          />
        </Grid>
      </Grid>

      {/* Daily Active Users - Full Width */}
      <Box sx={{ mb: 3 }}>
        <Card>
          <CardHeader title="Daily Active Users" />
          <CardContent>
            {loading.activeUsers ? (
              <Box display="flex" justifyContent="center" p={4}>
                <DWLFLoader size="medium" />
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={data.activeUsers || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke={c.ink(0.1)} />
                  <XAxis {...axis}
                    dataKey="date" 
                    tickFormatter={(value) => new Date(value).toLocaleDateString()} 
                  />
                  <YAxis {...axis} />
                  <Line type="monotone" dataKey="count" stroke={c.accent} strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Second Row: Device Types (50%) + Endpoints (25%) + Symbols (25%) */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
        {/* Device Types - 50% */}
        <Box sx={{ flex: '1 1 45%', minWidth: 300 }}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title="Device Types" />
            <CardContent>
              {loading.devices ? (
                <Box display="flex" justifyContent="center" p={4}>
                  <DWLFLoader size="medium" />
                </Box>
              ) : data.devices?.length ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={data.devices}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      dataKey="value"
                      label={({ label, percent }) => `${label} ${(percent * 100).toFixed(0)}%`}
                    >
                      {data.devices.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Typography variant="body2" color="text.secondary" align="center">
                  No device data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Popular Endpoints - 25% */}
        <Box sx={{ flex: '1 1 20%', minWidth: 200 }}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title="Popular Endpoints" />
            <CardContent>
              {loading.endpoints ? (
                <Box display="flex" justifyContent="center" p={4}>
                  <DWLFLoader size="medium" />
                </Box>
              ) : data.endpoints?.length ? (
                <List dense>
                  {data.endpoints.slice(0, 8).map((item, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                              {item.endpoint}
                            </Typography>
                            <Chip label={item.count} size="small" />
                          </Stack>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" align="center">
                  No endpoint data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Popular Symbols - 25% */}
        <Box sx={{ flex: '1 1 20%', minWidth: 200 }}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title="Popular Symbols" />
            <CardContent>
              {loading.symbols ? (
                <Box display="flex" justifyContent="center" p={4}>
                  <DWLFLoader size="medium" />
                </Box>
              ) : data.symbols?.length ? (
                <List dense>
                  {data.symbols.slice(0, 8).map((item, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" fontWeight="bold">
                              {item.symbol}
                            </Typography>
                            <Chip label={item.count} size="small" />
                          </Stack>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" align="center">
                  No symbol data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Grid container spacing={3}>

        {/* User Retention */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Card>
            <CardHeader title="User Retention (New vs Returning)" />
            <CardContent>
              {loading.retention ? (
                <Box display="flex" justifyContent="center" p={4}>
                  <DWLFLoader size="medium" />
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={data.retention || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke={c.ink(0.1)} />
                    <XAxis {...axis}
                      dataKey="date" 
                      tickFormatter={(value) => new Date(value).toLocaleDateString()} 
                    />
                    <YAxis {...axis} />
                    <Area 
                      type="monotone" 
                      dataKey="newUsers" 
                      stackId="1"
                      stroke={c.accent} 
                      fill={c.accent}
                      fillOpacity={0.8}
                      name="New Users"
                    />
                    <Area 
                      type="monotone" 
                      dataKey="returningUsers" 
                      stackId="1"
                      stroke={c.up} 
                      fill={c.up}
                      fillOpacity={0.8}
                      name="Returning Users"
                    />
                    <Legend />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Auth Methods */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Card>
            <CardHeader title="Authentication Methods" />
            <CardContent>
              {loading.authMethods ? (
                <Box display="flex" justifyContent="center" p={4}>
                  <DWLFLoader size="medium" />
                </Box>
              ) : data.authMethods?.length ? (
                <List dense>
                  {data.authMethods.map((item, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2">
                              {item.label}
                            </Typography>
                            <Chip label={item.value} size="small" />
                          </Stack>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" align="center">
                  No auth method data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Peak Hours Heatmap */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardHeader 
              title="Peak Hours (UTC)"
              action={
                <Tooltip title="Request volume by day of week and hour">
                  <IconButton>
                    <InfoOutlinedIcon />
                  </IconButton>
                </Tooltip>
              }
            />
            <CardContent>
              <PeakHoursHeatmap data={data.peakHours} loading={loading.peakHours} />
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Users */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardHeader title="Active Users Summary" />
            <CardContent>
              {loading.users ? (
                <Box display="flex" justifyContent="center" p={4}>
                  <DWLFLoader size="medium" />
                </Box>
              ) : data.users?.length ? (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Account ID</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Last Active</TableCell>
                        <TableCell>Total Requests</TableCell>
                        <TableCell>Auth Method</TableCell>
                        <TableCell>Top Endpoints</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.users.slice(0, 10).map((user) => (
                        <TableRow key={user.accountId}>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              {user.accountId}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {user.email || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDateTime(user.lastActive)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {user.totalRequests.toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={user.authMethod === 'jwt' ? 'Web' : user.authMethod === 'api_key' ? 'API' : user.authMethod} 
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1}>
                              {user.topEndpoints?.slice(0, 3).map((endpoint, index) => (
                                <Tooltip key={index} title={`${endpoint.endpoint} (${endpoint.count})`}>
                                  <Chip 
                                    label={endpoint.endpoint.split('/').pop() || endpoint.endpoint} 
                                    size="small" 
                                    variant="outlined"
                                  />
                                </Tooltip>
                              ))}
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Set Password">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setPasswordDialog({ open: true, accountId: user.accountId, email: user.email || '' });
                                  setNewPassword('');
                                }}
                              >
                                <LockResetIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary" align="center">
                  No user data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Set Password Dialog */}
      <Dialog open={passwordDialog.open} onClose={() => setPasswordDialog({ open: false, accountId: '', email: '' })} maxWidth="xs" fullWidth>
        <DialogTitle>Set Password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set a new password for {passwordDialog.email || passwordDialog.accountId}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            error={newPassword.length > 0 && newPassword.length < 8}
            helperText={newPassword.length > 0 && newPassword.length < 8 ? 'Must be at least 8 characters' : ''}
            onKeyDown={(e) => { if (e.key === 'Enter' && newPassword.length >= 8 && !passwordLoading) handleSetPassword(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialog({ open: false, accountId: '', email: '' })}>Cancel</Button>
          <Button onClick={handleSetPassword} variant="contained" disabled={passwordLoading || newPassword.length < 8}>
            {passwordLoading ? 'Setting...' : 'Set Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Analytics;